<template>
  <modal :show="show" :title="title" @close="closeGiveaway">
    <div class="giveaway" v-if="show">
      <template v-if="edit || create">
        <form class="d-f fd-r f-g p-x-4 p-t-4">
          <div class="f-1">
            <p class="m-b-1 card-content-title">
              <strong>Image</strong> <span>(optional)</span>
            </p>
            <media-upload size="h/200" title="Image" customText="Upload image" :chessboard="false" :multiple="false" :overwrite="true" :data="{ force: true, images: data.image ? [data.image] : [] }" @images="setGiveawayImage($event)" />
          </div>
          <div class="f-2">
            <div class="d-f ai-c">
              <p class="m-b-1 card-content-title"><strong>Title</strong></p>
              <span class="ml-a">{{ data.title.length }} / 40</span>
            </div>
            <form-input v-model="data.title" class="m-b-2" :maxlength="40" />
            <div class="d-f ai-c">
              <p class="m-b-1 card-content-title"><strong>Description</strong></p>
              <span class="ml-a">{{ data.description.length }} / 120</span>
            </div>
            <textarea :value="data.description" rows="3" @input="data.description = $event.target.value" maxlength="120"></textarea>
          </div>
        </form>
        <div class="p-x-4">
          <div class="d-f f-g fd-r m-t-2">
            <Button class="f-1" :disabled="!validGiveaway || loading" :loading="loading" @click="create ? createGiveaway() : updateGiveaway()">{{ create ? 'Create' : 'Save' }}</Button>
            <template v-if="!create">
              <Button class="f-1 outline" @click="cancelEditGiveaway">Cancel</Button>
            </template>
          </div>
        </div>
        <div class="p-x-4 p-b-4 p-t-2 ta-c appearance-text">
          <p>You can edit the default giveaway text and button labels under the <a href="/appearance/lvsp" target="_blank" class="link blue">Appearance</a> menu option.</p>
        </div>
      </template>
      <template v-else>
        <div class="giveaway-info" :data-state="giveaway.state">
          <div class="giveaway-about">
            <img :src="`https://${fqdn}.streamify.io/images/h/150/${giveaway.image}`" v-if="giveaway.image" />
            <div>
              <h2>{{ giveaway.title }}</h2>
              <p>{{ giveaway.description }}</p>
            </div>
          </div>
          <div class="giveaway-runtime">
            <template v-if="giveaway.state === 'published'">
              <p>Time elapsed</p>
              <p class="big m-t-1">{{ elapsed }}</p>
            </template>
            <p class="m-t-4">Contestants</p>
            <p class="big m-t-1">{{ totalContestants }}</p>
            <template v-if="giveaway.state === 'completed' && giveaway.participants">
              <p class="m-t-4">Winners</p>
              <ul class="m-t-1">
                <li><small class="blue" v-for="(winner, index) in giveaway.winners" :key="index"><span v-if="index !== 0">, </span>{{ winner.nickname }} ({{ winner.email }})</small></li>
              </ul>
            </template>
          </div>
        </div>
        <div class="giveaway-winners p-a-4" v-if="giveaway.state === 'closed'">
          <template v-if="giveaway.participants">
            <!-- eslint-disable-next-line -->
            <h3>Winner list ({{ winners.length }}/{{ giveaway.participants.length < 5 ? giveaway.participants.length : 5}})</h3>
            <ul v-if="winners.length > 0" class="m-t-2" :key="winnersKey">
              <li v-for="(winner, index) in winners" :key="winner.id || 'temp'">
                <template v-if="winner === ''">
                  <div class="d-f ai-c"><span class="loading"></span> <span style="color: #767676;">rolling winner</span></div>
                </template>
                <template v-else>
                  <div>
                    <p>{{ winner.nickname }}</p>
                    <span>{{ winner.email }}</span>
                  </div>
                  <Button @click="removeWinner(index)" class="only-text red keep-hover small" icon="trash-24">delete</Button>
                </template>
              </li>
            </ul>
            <template v-if="giveaway && giveaway.participants && giveaway.participants.length > 0">
              <Button @click="rollAWinner" :disabled="winners[winners.length] === ''" v-if="winners.length < 5 && giveaway.participants.length - winners.length > 0" class="only-text blue keep-hover no-side-padding" style="margin-top: 10px;" icon="plusCircle">Add {{ winners.length > 0 ? 'another' : 'a' }} winner</Button>
            </template>
            <template v-else>
              <p class="m-t-2">You dont have any participants to draw from.</p>
            </template>
          </template>
        </div>
        <div class="giveaway-buttons p-a-4">
          <template v-if="giveaway.state === 'unpublished'">
            <Button class="f-w" :disabled="giveaways.filter((g) => ['published', 'closed'].includes(g.state)).length > 0 || !['live'].includes(this.broadcast.status.life_cycle_status)" @click="updateGiveawayState('published')">Start now</Button>
            <div class="d-f f-g fd-r m-t-2">
              <Button icon="edit-pen2" class="outline f-1" @click="editGiveaway">Edit</Button>
              <Button icon="trash-24" class="red outline f-1" @click="deleteGiveaway">Delete</Button>
            </div>
          </template>
          <template v-else-if="giveaway.state === 'published'">
            <div class="d-f f-g fd-r m-t-2">
              <Button class="f-1" :loading="loading" @click="updateGiveawayState('closed')">End giveaway / draw winner</Button>
              <Button class="f-1 red outline" :loading="loading" @click="updateGiveawayState('unpublished')">Unpublish</Button>
            </div>
          </template>
          <template v-else-if="giveaway.state === 'closed'">
            <template v-if="giveaway && giveaway.participants && giveaway.participants.length === 0">
              <Button class="f-w" @click="updateGiveawayState('completed')">Complete giveaway without winners</Button>
            </template>
            <template v-else>
              <Button class="f-w" :disabled="winners.length === 0" @click="drawWinner()">Publish {{ winners.length > 1 ? 'winners' : 'winner' }}</Button>
            </template>
          </template>
        </div>
      </template>
    </div>
  </modal>
</template>

<script>

import moment from 'moment';

import MediaUpload from '@/components/MediaUpload.vue';

export default {
  components: {
    MediaUpload,
  },
  computed: {
    /**
     * Return the current active giveaway, if any.
     * @returns {?String}
     */
    active() {
      return this.$store.getters['Giveaway/active'];
    },
    /**
     * Return the current broadcast in store
     * @returns {?Object}
     */
    broadcast() {
      return this.$store.getters['Broadcast/current'];
    },
    /**
     * Get the current list of giveaways in store
     */
    giveaways() {
      return this.$store.getters['Giveaway/giveaways'];
    },
    create() {
      return this.$store.getters['Giveaway/create'];
    },
    edit() {
      return this.$store.getters['Giveaway/edit'];
    },
    giveaway() {
      return this.$store.getters['Giveaway/giveaway'];
    },
    /**
     * Return a boolean if the modal should be shown or not
     * @returns {Boolean}
     */
    show() {
      return !!this.create || !!this.edit || !!this.giveaway;
    },
    /**
     * Return the title that should be used depending on the current state of the giveaway
     * @returns {String}
     */
    title() {
      if (this.giveaway) {
        if (this.edit) {
          return 'Edit giveaway';
        }

        if (this.giveaway.state === 'closed') {
          return 'Draw winner(s)';
        }

        if (this.giveaway.state === 'unpublished') {
          return 'Not started';
        }

        if (this.giveaway.state === 'published') {
          return 'Active giveaway';
        }

        if (this.giveaway.state === 'completed') {
          return 'Completed giveaway';
        }

        return this.giveaway.title;
      }

      return 'Create giveaway';
    },
    validGiveaway() {
      return this.data.title && this.data.description;
    },
    totalContestants() {
      if (!this.giveaway.participants) {
        return 0;
      }

      if (this.giveaway.winners) {
        return this.giveaway.participants.length + this.giveaway.winners.length;
      }

      return this.giveaway.participants.length;
    },
  },
  methods: {
    removeWinner(index) {
      this.winners.splice(index, 1);
    },
    async rollAWinner() {
      const winner = await this.$store.dispatch('Giveaway/rollWinner', { id: this.giveaway.id, winners: this.winners.map(w => (w.id)) });

      this.winners.push('');

      setTimeout(() => {
        if (winner) {
          this.winners[this.winners.length - 1] = winner;
        } else {
          this.winners.pop();
        }
        this.winnersKey += 1;
      }, 1000);
    },
    async drawWinner() {
      if (this.winners.length === 0) {
        this.loading = false;
        return;
      }

      const result = await this.$store.dispatch('Giveaway/drawWinner', { id: this.giveaway.id, winners: this.winners.map(w => (w.id)) });

      if (result) {
        await this.$store.dispatch('Giveaway/updateState', { id: this.giveaway.id, state: 'completed' });
        this.$store.dispatch('Giveaway/getList', { broadcast_id: this.broadcast.id });
        this.winners = [];
        this.closeGiveaway();
      }
    },
    setGiveawayImage(images) {
      this.data.image = images.length ? images[0].value : '';
    },
    closeGiveaway() {
      this.$store.dispatch('Giveaway/clearGiveaway');
      this.data = {
        title: '',
        image: '',
        description: '',
      };
      this.winners = [];
    },
    editGiveaway() {
      this.$store.commit('Giveaway/editGiveaway', true);

      this.data = {
        title: this.giveaway.title,
        description: this.giveaway.description,
        image: this.giveaway.image,
      };
    },
    cancelEditGiveaway() {
      this.$store.commit('Giveaway/editGiveaway', false);
    },
    async deleteGiveaway() {
      const willDelete = await swal({
        title: this.$t('errors.are_you_sure'), text: 'This will remove giveaway', icon: 'warning', buttons: true, dangerMode: true,
      });

      if (!willDelete) {
        return;
      }

      const result = await this.$store.dispatch('Giveaway/deleteGiveaway', this.giveaway.id);
      if (result) {
        this.$store.dispatch('Giveaway/getList', { broadcast_id: this.broadcast.id });
        this.closeGiveaway();
      }
    },
    async createGiveaway() {
      const result = await this.$store.dispatch('Giveaway/createGiveaway', {
        id: this.broadcast.id,
        payload: this.data,
      });

      if (result) {
        await this.$store.dispatch('Giveaway/getList', { broadcast_id: this.broadcast.id });

        if (['live', 'liveStarting'].includes(this.broadcast.status.life_cycle_status)) {
          this.$store.commit('Giveaway/createGiveaway', false);
        } else {
          this.closeGiveaway();
        }
      }
    },
    async updateGiveaway() {
      this.loading = true;
      const result = await this.$store.dispatch('Giveaway/updateGiveaway', {
        id: this.giveaway.id,
        payload: this.data,
      });

      if (result) {
        this.loading = false;
        this.$store.dispatch('Giveaway/getList', { broadcast_id: this.broadcast.id });
        this.cancelEditGiveaway();
      }
    },
    async updateGiveawayState(state) {
      let swalText = '';
      let mutState = state;

      if (state === 'published') {
        swalText = `You can't edit the giveaway once it has started. Viewers can participate in the giveaway until you decide to end/draw the winner(s).\n
          You can unpublish the giveaway at any time.`;
      }

      if (state === 'closed') {
        swalText = 'When you choose to end the giveaway, your viewers can no longer participate. You then select one or more winners and publish the results.';

        if (this.giveaway.participants.length === 0) {
          swalText += '\n\nThere are no participants to draw a winner from. Are you sure you want to close the giveaway?';
          mutState = 'completed';
        }
      }

      const updateState = await swal({
        title: this.$t('common.are_you_sure'),
        text: swalText,
        buttons: true,
        className: 'center-content',
      });

      if (!updateState) {
        return;
      }

      this.loading = true;

      const result = await this.$store.dispatch('Giveaway/updateState', {
        id: this.giveaway.id,
        state: mutState,
      });

      this.loading = false;

      if (state === 'completed') {
        this.closeGiveaway();
      } else if (state === 'closed') {
        this.$store.dispatch('Giveaway/getGiveaway', { id: this.giveaway.id });
      } else if (state === 'published') {
        this.$store.dispatch('Giveaway/getGiveaway', { id: this.giveaway.id });
      }

      if (result) {
        this.$store.dispatch('Giveaway/getList', { broadcast_id: this.broadcast.id });
      }
    },
    timeupdate() {
      if (!this.active) {
        clearInterval(this.timeupdateInterval);
        return;
      }

      const a = new Date();
      const b = new Date(this.active.timestamp);
      const duration = moment.duration(Math.abs(b - a), 'millisecond');
      const hours = Math.floor(duration.asHours()) >= 10 ? Math.floor(duration.asHours()) : `0${Math.floor(duration.asHours())}`;
      const minutes = Math.floor(duration.minutes()) >= 10 ? Math.floor(duration.minutes()) : `0${Math.floor(duration.minutes())}`;
      const seconds = Math.floor(duration.seconds()) >= 10 ? Math.floor(duration.seconds()) : `0${Math.floor(duration.seconds())}`;
      this.elapsed = `${hours}:${minutes}:${seconds}`;
    },
    checkTimeupdate() {
      clearInterval(this.timeupdateInterval);
      if (this.giveaway && this.active && this.giveaway.id === this.active.id && this.giveaway.state === 'published') {
        this.timeupdateInterval = setInterval(() => {
          this.timeupdate();
        }, 1000);
        this.timeupdate();
      }
    },
  },
  watch: {
    active() {
      this.checkTimeupdate();
    },
    giveaway(giveaway) {
      if (!giveaway) {
        clearInterval(this.timeupdateInterval);
        this.elapsed = '00:00:00';
        return;
      }
      this.checkTimeupdate();
    },
  },
  data() {
    return {
      data: {
        title: '',
        image: '',
        description: '',
      },
      winners: [],
      timeupdateInterval: null,
      elapsed: '00:00:00',
      loading: false,
      winnersKey: 0,
    };
  },
};
</script>

<style lang="scss" scoped>
.giveaway {
  textarea {
    min-height: 75px;
    height: 75px;
    padding: 8px;
  }
}

.giveaway-info {
  padding: 20px;

  .giveaway-runtime {
    display: none;
    text-align: center;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    p {
      color: #464646;
      font-size: 16px;

      &.big {
        color: black;
        font-size: 42px;
        line-height: 50px;
      }
    }
  }

  .giveaway-about {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    justify-content: center;

    img {
      margin-bottom: 10px;
    }

    h2 {
      font-size: 20px;
      font-weight: 600;
      line-height: 32px;
    }

    p {
      line-height: 22px;
    }
  }

  &[data-state="published"], &[data-state="completed"] {
    display: flex;

    .giveaway-runtime {
      flex: 1;
      display: flex;
    }
    .giveaway-about {
      flex: 1.5;
    }
  }

  &[data-state="closed"] {
    border-bottom: 1px solid #f1f1f1;
    padding: 10px 20px;

    .giveaway-about {
      align-items: center;
      justify-content: flex-start;
      text-align: left;
      flex-direction: row;

      img {
        max-height: 70px;
        max-width: 70px;
        margin-bottom: 0;
        margin-right: 12px;
      }

      h2 {
        font-size: 16px;
        line-height: 22px;
      }
      p {
        font-size: 12px;
        line-height: 18px;
      }
    }
  }
}

.giveaway-winners {
  h3 {
    font-size: 16px;
  }

  ul {
    li {
      align-items: center;
      display: flex;
      padding: 5px 0;

      &:not(:last-child) {
        border-bottom: 1px solid #f1f1f1;
      }

      .button {
        margin-left: auto;
      }

      span {
        font-size: 12px;
      }
    }
  }
}

.giveaway-buttons {
  border-top: 1px solid #f1f1f1;
}

.giveaway-message {
  color: rgb(89, 89, 89);
  padding: 0 25px;
  text-align: center
}

.loading, .loading:after {
  border-radius: 50%;
  width: 18px;
  height: 18px;
}
.loading {
  margin-right: 9px;
  font-size: 10px;
  text-indent: -9999em;
  border-top: 2px solid rgba(255, 255, 255, 0.2);
  border-right: 2px solid rgba(255, 255, 255, 0.2);
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  border-left: 2px solid #000000;
  transform: translateZ(0);
  animation: spinner 1.1s infinite linear;
}
</style>

<style lang="scss">
.giveaway {
  .media-upload {
    .card-minimal {
      .card-content {
        background-color: transparent;
        border-radius: 0;
        box-shadow: none;
        & > div {
          padding: 0;
        }
      }
    }
    .upload-area .upload-text i {
      display: none;
    }
  }
}
</style>
